/**
 * IdpTestTab - Test IDP Authentication
 * Allows admins to test IDP flows directly from the dashboard
 */

import React, { useState, useEffect, useRef } from 'react';
import { getAuthConfig, getTokenInfo, verifyAppToken, listIdpConfigurations } from './idpApi';
import apiClient from '../../../assets/scripts/services/apiClient';

const IdpTestTab = ({ orgId, applications }) => {
  const [authConfig, setAuthConfig] = useState(null);
  const [idpConfigs, setIdpConfigs] = useState([]);
  const [selectedApp, setSelectedApp] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | authenticating | verifying | success | error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');

        // Load auth config and IDP configs in parallel
        const [config, idps] = await Promise.all([
          getAuthConfig(orgId).catch(() => null),
          listIdpConfigurations(orgId, true),
        ]);

        setAuthConfig(config);
        setIdpConfigs(Array.isArray(idps) ? idps : []);

        // Select first app by default
        if (applications.length > 0) {
          setSelectedApp(applications[0].id);
        }
      } catch (e) {
        console.error('Failed to load auth config:', e);
        setError('Failed to load authentication configuration');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [orgId, applications]);

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="idp-test-tab">
      <div className="d-flex justify-content-between align-items-center mB-20">
        <h5 className="mB-0">Test Authentication</h5>
      </div>

      {/* Configuration Summary */}
      <ConfigSummary authConfig={authConfig} idpConfigs={idpConfigs} />

      {/* Application Selector */}
      <div className="mB-20">
        <label className="form-label fw-bold">Test Application</label>
        <select
          className="form-select"
          value={selectedApp}
          onChange={(e) => setSelectedApp(e.target.value)}
        >
          <option value="">Select an application...</option>
          {applications.map((app) => (
            <option key={app.id} value={app.id}>
              {app.name || app.app_name || `Application ${app.id}`}
            </option>
          ))}
        </select>
        <div className="form-text">Select the application to test authentication against</div>
      </div>

      {/* Auth0 Test Module */}
      {authConfig && authConfig.provider_type === 'auth0' && selectedApp && (
        <Auth0TestModule
          config={authConfig}
          orgId={orgId}
          appId={selectedApp}
          onResult={setTestResult}
          onStatusChange={setStatus}
        />
      )}

      {/* Generic/Manual Test Module (for non-Auth0 or when no config) */}
      {selectedApp && (
        <ManualTokenTest
          orgId={orgId}
          appId={selectedApp}
          onResult={setTestResult}
          onStatusChange={setStatus}
          showDivider={authConfig?.provider_type === 'auth0'}
        />
      )}

      {/* Test Result Display */}
      <TestResultDisplay result={testResult} status={status} />
    </div>
  );
};

// Configuration Summary Component
const ConfigSummary = ({ authConfig, idpConfigs }) => {
  if (!authConfig && idpConfigs.length === 0) {
    return (
      <div className="alert alert-warning mB-20">
        <i className="ti-alert mR-10"></i>
        No authentication configuration found. Please configure an IDP first.
      </div>
    );
  }

  return (
    <div className="card mB-20">
      <div className="card-body">
        <h6 className="mB-15">Configuration Summary</h6>
        {authConfig && (
          <div className="row">
            <div className="col-md-6">
              <div className="small text-muted mB-5">
                <strong>Provider:</strong> {authConfig.provider_type?.toUpperCase() || 'Unknown'}
              </div>
              <div className="small text-muted mB-5">
                <strong>Domain:</strong> <code>{authConfig.domain || '—'}</code>
              </div>
              <div className="small text-muted mB-5">
                <strong>Client ID:</strong> <code>{authConfig.client_id || '—'}</code>
              </div>
            </div>
            <div className="col-md-6">
              <div className="small text-muted mB-5">
                <strong>Audience:</strong> <code>{authConfig.audience || '—'}</code>
              </div>
              <div className="small text-muted mB-5">
                <strong>Active IDPs:</strong> {idpConfigs.length}
              </div>
            </div>
          </div>
        )}
        {!authConfig && idpConfigs.length > 0 && (
          <div className="small text-muted">
            <strong>Active IDPs:</strong> {idpConfigs.length}
            <ul className="mB-0 mT-5">
              {idpConfigs.slice(0, 3).map((idp) => (
                <li key={idp.id}>{idp.name} ({idp.provider_type})</li>
              ))}
              {idpConfigs.length > 3 && <li>...and {idpConfigs.length - 3} more</li>}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// Auth0 Test Module Component
const Auth0TestModule = ({ config, orgId, appId, onResult, onStatusChange }) => {
  const popupRef = useRef(null);
  const callbackListenerRef = useRef(null);

  // Build Auth0 authorize URL
  const getAuthUrl = () => {
    const nonce = Math.random().toString(36).substring(2, 15);
    const params = new URLSearchParams({
      response_type: 'token id_token',
      client_id: config.client_id,
      redirect_uri: `${window.location.origin}/idp-test-callback.html`,
      scope: 'openid profile email',
      audience: config.audience,
      nonce: nonce,
      prompt: 'login', // Force fresh login every time (no session cache)
      max_age: '0', // Invalidate any existing session
    });
    return `https://${config.domain}/authorize?${params}`;
  };

  // Open Auth0 login in popup
  const startAuth0Test = () => {
    // Validate config before opening popup
    if (!config.domain || !config.client_id) {
      onStatusChange('error');
      onResult({
        error: `Missing Auth0 configuration. Domain: ${config.domain || 'missing'}, Client ID: ${config.client_id || 'missing'}`
      });
      return;
    }

    onResult(null);
    onStatusChange('authenticating');

    const authUrl = getAuthUrl();
    console.log('Auth0 URL:', authUrl); // Debug log

    const width = 500, height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    popupRef.current = window.open(
      authUrl,
      'auth0-test',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    // Set up callback listener
    callbackListenerRef.current = handleAuthCallback;
    window.addEventListener('message', callbackListenerRef.current);

    // Cleanup if popup is closed manually
    const checkClosed = setInterval(() => {
      if (popupRef.current?.closed) {
        clearInterval(checkClosed);
        if (callbackListenerRef.current) {
          window.removeEventListener('message', callbackListenerRef.current);
        }
      }
    }, 1000);
  };

  const handleAuthCallback = async (event) => {
    console.log('Received postMessage:', event.origin, event.data);

    if (event.origin !== window.location.origin) {
      console.log('Origin mismatch:', event.origin, 'vs', window.location.origin);
      return;
    }
    if (event.data?.type !== 'auth0-callback') {
      console.log('Not auth0-callback type:', event.data?.type);
      return;
    }

    console.log('Auth0 callback received, processing...');

    // Clean up listener
    if (callbackListenerRef.current) {
      window.removeEventListener('message', callbackListenerRef.current);
      callbackListenerRef.current = null;
    }
    popupRef.current?.close();

    const { access_token, error, error_description } = event.data;

    if (error) {
      console.log('Auth0 error:', error, error_description);
      onStatusChange('error');
      onResult({ error: error_description || error });
      return;
    }

    if (!access_token) {
      console.log('No access token in callback');
      onStatusChange('error');
      onResult({ error: 'No access token received' });
      return;
    }

    console.log('Calling verifyToken with appId:', appId);
    await verifyToken(access_token);
  };

  // Verify token with backend - uses /applications/{app}/auth/verify to register/login user
  const verifyToken = async (accessToken) => {
    console.log('verifyToken called with orgId:', orgId, 'appId:', appId);
    console.log('Token preview:', accessToken.substring(0, 50));
    onStatusChange('verifying');

    try {
      console.log('Calling verifyAppToken...');
      const data = await verifyAppToken(orgId, appId, accessToken);
      console.log('verifyAppToken response:', data);
      onStatusChange('success');
      onResult({
        success: true,
        verified: true,
        user: data.user,
        jit_provisioned: data.jit_provisioned,
        token_preview: accessToken.substring(0, 50) + '...',
      });
    } catch (err) {
      console.error('verifyAppToken error:', err);
      onStatusChange('error');
      onResult({
        success: false,
        error: err.message || 'Token verification failed',
      });
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (callbackListenerRef.current) {
        window.removeEventListener('message', callbackListenerRef.current);
      }
      popupRef.current?.close();
    };
  }, []);

  return (
    <div className="auth0-test-module mB-20">
      <div className="card">
        <div className="card-body">
          <h6 className="mB-15">
            <i className="ti-id-badge mR-10 c-orange-500"></i>
            Auth0 Login Test
          </h6>
          <p className="text-muted small mB-15">
            Click the button below to open an Auth0 login popup. After successful authentication,
            the token will be automatically verified against your backend.
          </p>
          <button className="btn btn-primary" onClick={startAuth0Test}>
            <i className="ti-lock mR-10"></i>
            Login with Auth0
          </button>

          <div className="alert alert-info mT-15 small mB-0">
            <strong>Note:</strong> Ensure your Auth0 application has the following callback URL configured:
            <br />
            <code>{window.location.origin}/idp-test-callback.html</code>
          </div>
        </div>
      </div>
    </div>
  );
};

// Manual Token Test Component
const ManualTokenTest = ({ orgId, appId, onResult, onStatusChange, showDivider }) => {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);

  const testManualToken = async () => {
    if (!token.trim()) return;

    setLoading(true);
    onResult(null);
    onStatusChange('verifying');

    try {
      const data = await verifyAppToken(orgId, appId, token.trim());
      onStatusChange('success');
      onResult({
        success: true,
        verified: true,
        user: data.user,
        jit_provisioned: data.jit_provisioned,
      });
    } catch (err) {
      onStatusChange('error');
      onResult({
        success: false,
        error: err.message || 'Token verification failed',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manual-token-test mB-20">
      {showDivider && (
        <div className="d-flex align-items-center mB-20">
          <hr className="flex-grow-1" />
          <span className="px-3 text-muted small">OR</span>
          <hr className="flex-grow-1" />
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <h6 className="mB-15">
            <i className="ti-key mR-10"></i>
            Manual Token Test
          </h6>
          <p className="text-muted small mB-15">
            Paste a JWT access token to verify it against your application's IDP configuration.
          </p>

          <div className="mB-15">
            <label className="form-label">JWT Token</label>
            <textarea
              className="form-control font-monospace"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6Ii4uLiJ9..."
              rows={4}
            />
          </div>

          <button
            className="btn btn-outline-primary"
            onClick={testManualToken}
            disabled={!token.trim() || loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mR-5"></span>
                Verifying...
              </>
            ) : (
              <>
                <i className="ti-check mR-5"></i>
                Verify Token
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Test Result Display Component
const TestResultDisplay = ({ result, status }) => {
  if (status === 'idle' || !result) return null;

  if (status === 'authenticating') {
    return (
      <div className="alert alert-info">
        <div className="d-flex align-items-center">
          <span className="spinner-border spinner-border-sm mR-10"></span>
          Waiting for Auth0 login...
        </div>
      </div>
    );
  }

  if (status === 'verifying') {
    return (
      <div className="alert alert-info">
        <div className="d-flex align-items-center">
          <span className="spinner-border spinner-border-sm mR-10"></span>
          Verifying token with backend...
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="alert alert-danger">
        <h6 className="alert-heading mB-10">
          <i className="ti-close mR-10"></i>
          Authentication Failed
        </h6>
        <p className="mB-0">{result?.error || 'Unknown error occurred'}</p>
      </div>
    );
  }

  if (status === 'success' && result?.success) {
    return (
      <div className="alert alert-success">
        <h6 className="alert-heading mB-10">
          <i className="ti-check mR-10"></i>
          Token Verified!
        </h6>

        {/* IDP Info */}
        {result.idp_name && (
          <div className="mB-10">
            <span className="badge bg-primary me-2">{result.idp_name}</span>
            {result.verified && <span className="badge bg-success">Verified</span>}
          </div>
        )}

        {/* Token Headers */}
        {result.headers && (
          <div className="mB-10">
            <strong>Headers:</strong>
            <div className="small font-monospace bgc-grey-100 p-2 bdrs-3 mT-5">
              <div>alg: {result.headers.alg}</div>
              <div>typ: {result.headers.typ}</div>
              {result.headers.kid && <div>kid: {result.headers.kid}</div>}
            </div>
          </div>
        )}

        {/* Claims */}
        {result.claims && (
          <div className="mB-10">
            <strong>Claims:</strong>
            <div className="small font-monospace bgc-grey-100 p-2 bdrs-3 mT-5">
              {result.claims.sub && <div>sub: {result.claims.sub}</div>}
              {result.claims.email && <div>email: {result.claims.email}</div>}
              {result.claims.name && <div>name: {result.claims.name}</div>}
              {result.claims.iss && <div>iss: {result.claims.iss}</div>}
              {result.claims.aud && (
                <div>aud: {Array.isArray(result.claims.aud) ? result.claims.aud.join(', ') : result.claims.aud}</div>
              )}
              {result.claims.scope && <div>scope: {result.claims.scope}</div>}
            </div>
          </div>
        )}

        {/* User info if available */}
        {result.user && (
          <div className="mB-10">
            <strong>User:</strong>
            <div className="small">
              <div>ID: {result.user.id}</div>
              {result.user.email && <div>Email: {result.user.email}</div>}
              {result.user.user_name && <div>Name: {result.user.user_name}</div>}
              {result.user.user_type && <div>Type: {result.user.user_type}</div>}
            </div>
          </div>
        )}

        {result.jit_provisioned && (
          <span className="badge bg-info">
            <i className="ti-user mR-5"></i>
            JIT Provisioned (New User)
          </span>
        )}

        {result.token_preview && (
          <div className="mT-10 small text-muted">
            <strong>Token:</strong> <code>{result.token_preview}</code>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default IdpTestTab;
